import { t } from '@bia-auction/shared-i18n';
import { Button } from '@bia-auction/ui';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';

/**
 * A secret the server will never show again — currently a freshly minted firm API key
 * (M13). Only the hash is stored, so if the operator closes the dialog without copying it,
 * the key is gone and must be re-issued. Hence the deliberate weight: monospace, selectable,
 * a copy button that confirms, and the warning as body copy rather than a toast that
 * disappears.
 *
 * App-local on purpose: the bidder app has no secrets to show, so this stays out of
 * `packages/ui` until a second caller exists.
 */
export function SecretOnce({ value, warning }: { value: string; warning: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      // Revert the confirmation so a second copy still reads as an action, not a state.
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard denied (insecure context / permission) — the value is selectable on screen,
      // which is the fallback. Never leave the button stuck in "copied".
      setCopied(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <code className="min-w-0 flex-1 select-all break-all rounded-md border bg-muted px-3 py-2 font-mono text-sm">
          {value}
        </code>
        <Button type="button" variant="outline" size="sm" onClick={() => void copy()}>
          {copied ? (
            <Check aria-hidden className="mr-1 h-4 w-4" />
          ) : (
            <Copy aria-hidden className="mr-1 h-4 w-4" />
          )}
          {copied ? t('common.copied') : t('common.copy')}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">{warning}</p>
    </div>
  );
}
